import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { Flex, Button, Space } from "antd"
import { TextArea } from "ui/Input"
import { Text, Title } from "ui/Typography"
import { useDispatch } from "react-redux"
import { addClientNote, updateClientNote, getClientNotes } from "store/clients"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import PropTypes from "prop-types"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"

const AddEditModal = ({ open, setOpen, loading, handleCancel, editMode, noteValue, setNoteValue, clientId, currentNote }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const initialNoteValue = useMemo(() => (editMode ? currentNote?.content || "" : ""), [editMode, currentNote])

  const hasUnsavedChanges = noteValue !== initialNoteValue

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
      return
    }

    handleCancel()
  }

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false)
    handleCancel()
  }

  const handleDrawerOpenChange = (nextOpen) => {
    if (nextOpen) {
      setOpen(true)
      return
    }

    handleRequestClose()
  }

  const handleSave = async () => {
    if (editMode) {
      await dispatch(
        updateClientNote(currentNote.id, {
          note: {
            client_id: Number(clientId),
            content: noteValue
          }
        })
      )
    } else {
      await dispatch(
        addClientNote({
          note: {
            client_id: Number(clientId),
            content: noteValue
          }
        })
      )
    }
    await dispatch(getClientNotes({ client_id: Number(clientId) }))
    handleCancel()
  }

  if (loading) return null

  const isNoteValid = noteValue.length <= 1000

  return (
    <>
      {isMobile ? <CustomDrawer open={open} setOpen={handleDrawerOpenChange} title={editMode ? t("clients.notes.edit_note") : t("clients.notes.add_note")}>
        <Space direction="vertical" size={36} className="w-full">
          <Flex vertical gap={4}>
            <Text strong>{t("clients.notes.label")}</Text>
            <TextArea
              placeholder={t("clients.notes.placeholder")}
              rows={4}
              value={noteValue}
              maxLength={1000}
              showCount={{ max: 1000 }}
              onChange={(e) => {
                if (e.target.value.length <= 1000) setNoteValue(e.target.value)
              }}
              name="note"
            />
          </Flex>
          <Flex gap={12}>
            <Button variant="outlined" color="primary" onClick={handleRequestClose}>
              {t("clients.notes.cancel")}
            </Button>
            <Button type="primary" disabled={!noteValue || loading || !isNoteValid} onClick={handleSave}>
              {t("clients.notes.save")}
            </Button>
          </Flex>
        </Space>
      </CustomDrawer> :
        <Modal centered open={open} onCancel={handleRequestClose} loading={loading} width={800}>
          <Space direction="vertical" size={36} className="w-full">
            <Title level={3}>{editMode ? t("clients.notes.edit_note") : t("clients.notes.add_note")}</Title>
            <Flex vertical gap={4}>
              <Text strong>{t("clients.notes.label")}</Text>
              <TextArea
                placeholder={t("clients.notes.placeholder")}
                rows={4}
                value={noteValue}
                maxLength={1000}
                showCount={{ max: 1000 }}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) setNoteValue(e.target.value)
                }}
                name="note"
              />
            </Flex>
            <Flex gap={12}>
              <Button variant="outlined" color="primary" onClick={handleRequestClose}>
                {t("clients.notes.cancel")}
              </Button>
              <Button type="primary" disabled={!noteValue || loading || !isNoteValid} onClick={handleSave}>
                {t("clients.notes.save")}
              </Button>
            </Flex>
          </Space>
        </Modal>}

      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscardChanges} onKeepEditing={() => setShowUnsavedModal(false)} />
    </>
  )
}

AddEditModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  loading: PropTypes.bool,
  handleCancel: PropTypes.func,
  editMode: PropTypes.bool,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  clientId: PropTypes.string,
  currentNote: PropTypes.object
}

export default AddEditModal
